/**
 * Utility functions for parsing Tally XML and Excel files
 * Extracted from TDSImport.jsx for better maintainability
 */

// Format Tally date (DD-Mon-YYYY or D-Mon-YY) to YYYY-MM-DD
export const formatTallyDate = (dateStr) => {
  if (!dateStr) return '';
  
  const months = {
    'jan': '01', 'feb': '02', 'mar': '03', 'apr': '04',
    'may': '05', 'jun': '06', 'jul': '07', 'aug': '08',
    'sep': '09', 'oct': '10', 'nov': '11', 'dec': '12'
  };

  // Try DD-Mon-YYYY or D-Mon-YY format
  const match = dateStr.match(/(\d{1,2})-([A-Za-z]{3})-(\d{2,4})/);
  if (match) {
    const day = match[1].padStart(2, '0');
    const month = months[match[2].toLowerCase()] || '01';
    let year = match[3];
    if (year.length === 2) {
      year = parseInt(year) > 50 ? '19' + year : '20' + year;
    }
    return `${year}-${month}-${day}`;
  }
  
  return dateStr;
};

// Extract TDS section from filename
export const extractSection = (filename) => {
  if (!filename) return '';
  
  // Common patterns: "194C", "194J", "194H", etc.
  const sectionMatch = filename.match(/\b(194[A-Z0-9]+|206C[A-Z0-9]*)\b/i);
  if (sectionMatch) {
    return sectionMatch[1].toUpperCase();
  }
  
  // Try to extract from keywords
  const sectionKeywords = {
    'contractor': '194C',
    'professional': '194J',
    'commission': '194H',
    'rent': '194I',
    'interest': '194A',
    'dividend': '194',
    'tcs': '206C'
  };
  
  const lowerFilename = filename.toLowerCase();
  for (const [keyword, section] of Object.entries(sectionKeywords)) {
    if (lowerFilename.includes(keyword)) {
      return section;
    }
  }
  
  return '';
};

// Extract voucher number from string like "(No. :124)" -> "124"
export const extractVoucherNo = (str) => {
  if (!str) return '';
  const match = str.match(/\(No\.\s*:\s*([^)]+)\)/i);
  if (match) return match[1].trim();
  // Try other patterns
  const numMatch = str.match(/(\d+)/);
  return numMatch ? numMatch[1] : str.trim();
};

// Derive PAN from GSTIN (characters 3-12)
export const derivePanFromGstin = (gstin) => {
  if (!gstin || gstin.length < 12) return null;
  return gstin.substring(2, 12).toUpperCase();
};

// Check if PAN belongs to a company (4th character is 'C')
export const isPanCompany = (pan) => {
  if (!pan || pan.length < 4) return false;
  return pan.charAt(3).toUpperCase() === 'C';
};

// Auto-detect parent voucher type from name patterns
export const detectParentVoucherType = (vchName) => {
  if (!vchName) return null;
  const name = vchName.trim().toLowerCase();
  
  const patterns = [
    { pattern: /^purchase/i, parent: 'purchase' },
    { pattern: /^sales/i, parent: 'sales' },
    { pattern: /^journal/i, parent: 'journal' },
    { pattern: /^payment/i, parent: 'payment' },
    { pattern: /^receipt/i, parent: 'receipt' },
    { pattern: /^contra/i, parent: 'contra' },
    { pattern: /^credit note/i, parent: 'credit note' },
    { pattern: /^debit note/i, parent: 'debit note' },
    { pattern: /purchase.*return/i, parent: 'debit note' },
    { pattern: /sales.*return/i, parent: 'credit note' },
    { pattern: /gst.*purchase/i, parent: 'purchase' },
    { pattern: /gst.*sales/i, parent: 'sales' },
  ];
  
  for (const { pattern, parent } of patterns) {
    if (pattern.test(name)) {
      return parent;
    }
  }
  
  // Check for common suffixes/prefixes
  if (name.includes('pur') && !name.includes('sales')) return 'purchase';
  if (name.includes('sal') && !name.includes('pur')) return 'sales';
  if (name.includes('jrnl') || name.includes('jv')) return 'journal';
  
  return null;
};

// Parse TDS Ledger XML (non-columnar format)
export const parseTDSLedgerXML = (xmlText, filename) => {
  const entries = [];
  const sectionFromFilename = extractSection(filename) || '';
  
  // Clean XML
  let cleanXml = xmlText.replace(/^\uFEFF/, '').replace(/^\xEF\xBB\xBF/, '');
  
  // Parse entries - each set has date, ledger, type, dr/cr amt, vch number
  const dateMatches = cleanXml.match(/<DSPVCHDATE>([^<]*)<\/DSPVCHDATE>/gi) || [];
  const ledgerMatches = cleanXml.match(/<DSPVCHLEDACCOUNT>([^<]*)<\/DSPVCHLEDACCOUNT>/gi) || [];
  const typeMatches = cleanXml.match(/<DSPVCHTYPE>([^<]*)<\/DSPVCHTYPE>/gi) || [];
  const drAmtMatches = cleanXml.match(/<DSPVCHDRAMT>([^<]*)<\/DSPVCHDRAMT>/gi) || [];
  const crAmtMatches = cleanXml.match(/<DSPVCHCRAMT>([^<]*)<\/DSPVCHCRAMT>/gi) || [];
  const vchNoMatches = cleanXml.match(/<DSPEXPLVCHNUMBER>([^<]*)<\/DSPEXPLVCHNUMBER>/gi) || [];
  
  const extractValue = (match) => {
    if (!match) return '';
    const m = match.match(/>([^<]*)</);
    return m ? m[1].trim() : '';
  };
  
  for (let i = 0; i < dateMatches.length; i++) {
    const date = extractValue(dateMatches[i]);
    const ledger = extractValue(ledgerMatches[i]);
    const vchType = extractValue(typeMatches[i]);
    const drAmt = parseFloat(extractValue(drAmtMatches[i])?.replace(/,/g, '')) || 0;
    const crAmt = parseFloat(extractValue(crAmtMatches[i])?.replace(/,/g, '')) || 0;
    const vchNoRaw = extractValue(vchNoMatches[i]);
    const vchNo = extractVoucherNo(vchNoRaw);
    
    // Skip Payment voucher types
    const vchTypeLower = vchType.toLowerCase();
    if (vchTypeLower === 'pymt' || vchTypeLower === 'payment' || 
        vchTypeLower.includes('bank payment') || vchTypeLower.includes('cash payment')) {
      continue;
    }
    
    // Skip if ledger contains BANK
    if (ledger.toUpperCase().includes('BANK')) {
      continue;
    }
    
    // Determine TDS amount
    let tdsAmount = 0;
    let isReversal = false;
    
    if (crAmt > 0) {
      tdsAmount = crAmt;
    } else if (drAmt !== 0) {
      tdsAmount = -Math.abs(drAmt);
      isReversal = true;
    }
    
    if (tdsAmount !== 0 && vchNo) {
      entries.push({
        date: formatTallyDate(date),
        party_name: ledger,
        voucher_no: vchNo,
        voucher_type: vchType,
        tds_amount: tdsAmount,
        section: sectionFromFilename,
        source: 'tds_ledger',
        is_reversal: isReversal
      });
    }
  }
  
  return entries;
};

// Parse voucher entries from columnar XML format
export const parseColumnarVoucherXML = (xmlText) => {
  const vouchers = [];
  
  // Clean XML
  let cleanXml = xmlText.replace(/^\uFEFF/, '').replace(/^\xEF\xBB\xBF/, '');
  
  // Find all TALLYMESSAGE blocks with VOUCHER
  const voucherBlocks = cleanXml.match(/<VOUCHER[^>]*>[\s\S]*?<\/VOUCHER>/gi) || [];
  
  for (const block of voucherBlocks) {
    // Extract voucher number
    const vchNoMatch = block.match(/VOUCHERNUMBER="([^"]+)"|<VOUCHERNUMBER>([^<]+)/i);
    const vchNo = vchNoMatch ? (vchNoMatch[1] || vchNoMatch[2]) : '';
    
    if (!vchNo) continue;
    
    // Extract date
    const dateMatch = block.match(/DATE="(\d+)"|<DATE>(\d+)/i);
    let date = '';
    if (dateMatch) {
      const dateStr = dateMatch[1] || dateMatch[2];
      if (dateStr.length === 8) {
        date = `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
      }
    }
    
    // Extract voucher type
    const vchTypeMatch = block.match(/VCHTYPE="([^"]+)"|<VOUCHERTYPE>([^<]+)/i);
    const vchType = vchTypeMatch ? (vchTypeMatch[1] || vchTypeMatch[2]) : '';
    
    // Extract party name (PARTYLEDGERNAME)
    const partyMatch = block.match(/<PARTYLEDGERNAME>([^<]+)/i);
    const partyName = partyMatch ? partyMatch[1] : '';
    
    // Extract amount from ALLLEDGERENTRIES or LEDGERENTRIES
    let amount = 0;
    const amtMatch = block.match(/<AMOUNT>([^<]+)/i);
    if (amtMatch) {
      amount = Math.abs(parseFloat(amtMatch[1].replace(/,/g, '')) || 0);
    }
    
    if (vchNo && (partyName || amount > 0)) {
      vouchers.push({
        voucher_no: vchNo,
        date,
        voucher_type: vchType,
        party_name: partyName,
        amount
      });
    }
  }
  
  return vouchers;
};

// Check for PAN mismatches between XML and database
export const checkPanMismatches = (transactions, existingParties) => {
  const mismatches = [];
  const missingParty = [];
  const amtMismatches = [];
  
  transactions.forEach((txn, index) => {
    const xmlPan = (txn.pan || '').toUpperCase().trim();
    const partyName = (txn.party_name || '').trim();
    
    // Find matching party in database (case-insensitive)
    const dbParty = existingParties.find(p => 
      p.name?.toLowerCase() === partyName.toLowerCase()
    );
    
    if (dbParty) {
      const dbPan = (dbParty.pan || '').toUpperCase().trim();
      // Check for mismatch (both have PAN but different)
      if (xmlPan && dbPan && xmlPan !== dbPan) {
        mismatches.push({
          index,
          party_name: partyName,
          xml_pan: xmlPan,
          db_pan: dbPan,
          voucher_no: txn.voucher_no,
          tds_amount: txn.tds_amount
        });
      }
    }
    
    // Track entries with missing PAN or party name
    if (!xmlPan || !partyName) {
      missingParty.push({
        index,
        party_name: partyName || '',
        pan: xmlPan,
        voucher_no: txn.voucher_no,
        tds_amount: txn.tds_amount
      });
    }
    
    // Track amount mismatches (Amount=0 but TDS/TCS>0)
    const amount = txn.amount || 0;
    const tdsAmount = Math.abs(txn.tds_amount || 0);
    if (amount === 0 && tdsAmount > 0) {
      amtMismatches.push({
        index,
        party_name: partyName,
        amount,
        tds_amount: txn.tds_amount,
        voucher_no: txn.voucher_no
      });
    }
  });
  
  return { mismatches, missingParty, amtMismatches };
};

export default {
  formatTallyDate,
  extractSection,
  extractVoucherNo,
  derivePanFromGstin,
  isPanCompany,
  detectParentVoucherType,
  parseTDSLedgerXML,
  parseColumnarVoucherXML,
  checkPanMismatches
};
